import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HiChevronDown, HiChevronRight } from "react-icons/hi";
import { courseTrainersApi } from "api/courseTrainersApi";
import { CourseTrainerApi, CourseTrainerPriceApi } from "api/types";
import { PaziaLoader } from "components/atoms";
import { EmptyState } from "components/molecules";
import { routes } from "consts";
import { formatDate, fromTimeFormat } from "utils";
import { groupPricesBySession } from "./sessionGrouping";

type SummaryMode = "sum" | "session" | "day";

const MODE_OPTIONS: { value: SummaryMode; label: string }[] = [
  { value: "sum", label: "Suma" },
  { value: "session", label: "Per zjazd" },
  { value: "day", label: "Per dzień" },
];

interface TrainerHoursSummaryProps {
  courseId: number;
}
export const TrainerHoursSummary = ({ courseId }: TrainerHoursSummaryProps) => {
  const [mode, setMode] = useState<SummaryMode>("sum");
  const { isLoading, data } = useQuery({
    queryKey: ["fetchTrainersByCourseId", courseId],
    queryFn: () => courseTrainersApi.fetchByCourseId(courseId),
  });

  const trainers = useMemo(
    () =>
      [...(data?.data ?? [])].sort(
        (a, b) => a.surname.localeCompare(b.surname, "pl") || a.firstName.localeCompare(b.firstName, "pl"),
      ),
    [data],
  );

  if (isLoading) return <PaziaLoader />;

  const renderContent = () => {
    if (trainers.length === 0) {
      return (
        <EmptyState
          title="Brak informacji"
          description="Przypisz trenerów do zjazdów w harmonogramie, aby zobaczyć podsumowanie godzin."
          centerDescription
        />
      );
    }
    if (mode === "session") return <SessionHoursList trainers={trainers} />;
    return trainers.map((trainer) => <TrainerHoursRow key={trainer.id} trainer={trainer} mode={mode} />);
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Godziny trenerów</h3>
        <div className="flex gap-1">
          {MODE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setMode(option.value)}
              className={`rounded px-3 py-1 text-sm ${
                mode === option.value
                  ? "bg-primary-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {renderContent()}
    </div>
  );
};

const TrainerLink = ({ trainer }: { trainer: CourseTrainerApi }) => (
  <Link
    to={routes.TRAINER(trainer.id)}
    className="hover:text-primary-600 underline"
    onClick={(event) => event.stopPropagation()}
  >
    {trainer.firstName} {trainer.surname}
  </Link>
);

interface TrainerHoursRowProps {
  trainer: CourseTrainerApi;
  mode: Exclude<SummaryMode, "session">;
}
const TrainerHoursRow = ({ trainer, mode }: TrainerHoursRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const totalHours = useMemo(() => sumHours(trainer.prices), [trainer.prices]);
  const days = useMemo(() => groupPricesByDay(trainer.prices), [trainer.prices]);
  const sessions = useMemo(() => groupPricesBySession(trainer.prices), [trainer.prices]);

  const isExpandable = mode === "sum" && trainer.prices.length > 0;
  const toggle = () => isExpandable && setExpanded((prev) => !prev);
  const Chevron = expanded ? HiChevronDown : HiChevronRight;

  return (
    <div className="mb-2 rounded border border-slate-200 dark:border-slate-700">
      <div
        className={`flex items-center justify-between bg-slate-100 p-2 dark:bg-slate-700 dark:text-slate-300 ${
          isExpandable ? "cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-600" : ""
        }`}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
          }
        }}
        role={isExpandable ? "button" : undefined}
        tabIndex={isExpandable ? 0 : undefined}
        aria-expanded={isExpandable ? expanded : undefined}
      >
        <span className="flex items-center gap-1">
          {isExpandable && <Chevron className="h-4 w-4 shrink-0" />}
          <TrainerLink trainer={trainer} />
        </span>
        <span className="font-semibold">{totalHours.toFixed(2)} h</span>
      </div>
      {mode === "day" && (
        <ul className="list-inside list-disc p-2 text-sm">
          {days.map((day) => (
            <li key={day.label} className="mb-1">
              {day.label} &mdash; <span className="font-semibold">{day.hours.toFixed(2)} h</span>
            </li>
          ))}
        </ul>
      )}
      {mode === "sum" && expanded && (
        <div className="p-2 text-sm">
          {sessions.map((session) => (
            <div key={session.courseSessionId} className="mb-2 last:mb-0">
              <div className="flex justify-between font-semibold text-slate-700 dark:text-slate-200">
                <span>{session.label}</span>
                <span>{session.hours.toFixed(2)} h</span>
              </div>
              <ul className="list-inside list-disc pl-2 text-slate-600 dark:text-slate-300">
                {sortPricesChronologically(session.prices).map((price, index) => (
                  <li key={`${price.courseScheduleId}-${index}`} className="mb-0.5">
                    {formatDate(price.date, false, false, "short")}, {price.timeFrom}&ndash;{price.timeTo}
                    {price.topicName && <> &middot; {price.topicName}</>} &mdash;{" "}
                    <span className="font-semibold">{getHours(price).toFixed(2)} h</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// "Per zjazd" view: one block per zjazd (across all trainers), with each trainer's hours in it.
const SessionHoursList = ({ trainers }: { trainers: CourseTrainerApi[] }) => {
  const sessions = useMemo(() => {
    const allPrices = trainers.flatMap((trainer) => trainer.prices);
    return groupPricesBySession(allPrices).map((session) => ({
      ...session,
      trainers: trainers
        .map((trainer) => ({
          trainer,
          hours: sumHours(trainer.prices.filter((price) => price.courseSessionId === session.courseSessionId)),
        }))
        .filter(({ hours }) => hours > 0),
    }));
  }, [trainers]);

  if (sessions.length === 0) {
    return (
      <EmptyState
        title="Brak informacji"
        description="Przypisz trenerów do zjazdów w harmonogramie, aby zobaczyć podsumowanie godzin."
        centerDescription
      />
    );
  }

  return (
    <>
      {sessions.map((session) => (
        <div key={session.courseSessionId} className="mb-2 rounded border border-slate-200 dark:border-slate-700">
          <div className="flex justify-between bg-slate-100 p-2 dark:bg-slate-700 dark:text-slate-300">
            <span>{session.label}</span>
            <span className="font-semibold">{session.hours.toFixed(2)} h</span>
          </div>
          <ul className="list-inside list-disc p-2 text-sm">
            {session.trainers.map(({ trainer, hours }) => (
              <li key={trainer.id} className="mb-1">
                <TrainerLink trainer={trainer} /> &mdash; <span className="font-semibold">{hours.toFixed(2)} h</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
};

const getHours = (price: CourseTrainerPriceApi) => (fromTimeFormat(price.timeTo) - fromTimeFormat(price.timeFrom)) / 60;

const sumHours = (prices: CourseTrainerPriceApi[]) => prices.reduce((acc, price) => acc + getHours(price), 0);

const sortPricesChronologically = (prices: CourseTrainerPriceApi[]) =>
  [...prices].sort(
    (a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime() ||
      fromTimeFormat(a.timeFrom) - fromTimeFormat(b.timeFrom),
  );

const groupPricesByDay = (prices: CourseTrainerPriceApi[]) => {
  const byDay = new Map<string, { label: string; hours: number }>();
  prices.forEach((price) => {
    const label = formatDate(price.date, false, false, "short");
    const existing = byDay.get(label);
    if (existing) {
      existing.hours += getHours(price);
    } else {
      byDay.set(label, { label, hours: getHours(price) });
    }
  });
  return Array.from(byDay.values());
};
